/**
 * Dorie Atelier — Cart v2 Enhancements
 *
 * Features
 *   1. "Reserved · 09:47 remaining" countdown
 *      - 10-minute default (configurable via [data-minutes])
 *      - Persisted in sessionStorage so it survives page navigation
 *      - Auto-resets to full duration the moment it hits zero
 *      - Pauses when the tab is hidden, resumes on focus
 *      - Uses in-place text-node mutation to avoid MutationObserver feedback loops
 *
 *   2. Complimentary shipping progress bar (multi-currency)
 *      - Reads base threshold and base currency from data attributes on the bar
 *      - Fetches /cart.js to get cart.total_price in the *presentment* currency
 *      - Converts the base threshold to the presentment currency using
 *        window.Shopify.currency.rate when available (Shopify Markets / currency
 *        picker themes), else falls back to the base value
 *      - Formats display via Intl.NumberFormat for proper i18n
 *      - Re-runs on every cart mutation (fetch monkey-patch + MutationObserver)
 *
 * Scoping
 *   This file self-scopes to elements that carry data-dorie-countdown or
 *   data-dorie-shipping-bar. If those elements are not present on a page
 *   (e.g. product detail, homepage), the script is effectively a no-op.
 *
 * Loaded from snippets/cart-summary.liquid as <script defer>. Nothing in
 * layout/theme.liquid or any non-cart file is touched.
 */
(function () {
  'use strict';

  // ────────────────────────────────────────────────
  // CONFIG
  // ────────────────────────────────────────────────

  var COUNTDOWN_KEY = 'dorieCartReservedEndsAt';
  var DEFAULT_MINUTES = 10;
  var REINIT_DEBOUNCE_MS = 120;
  var CART_ENDPOINT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart.js';

  // URL fragments that indicate a cart mutation happened and we should refetch
  var CART_MUTATION_URLS = ['/cart/add', '/cart/update', '/cart/change', '/cart/clear'];

  // ────────────────────────────────────────────────
  // STATE
  // ────────────────────────────────────────────────

  var tickTimer = null;
  var reinitTimer = null;
  var fetchPatched = false;
  var observersAttached = false;

  // ────────────────────────────────────────────────
  // UTIL
  // ────────────────────────────────────────────────

  /**
   * Mutates a text node in place. Avoids firing childList mutations so we don't
   * bounce-trigger our own MutationObserver every time the countdown ticks.
   */
  function setText(el, text) {
    if (!el) return;
    var child = el.firstChild;
    if (child && child.nodeType === 3) {
      if (child.data !== text) child.data = text;
    } else {
      el.textContent = text;
    }
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(navigator.language || 'en-US', {
        style: 'currency',
        currency: currency || 'CAD',
        maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch (_) {
      return Math.round(cents / 100) + ' ' + (currency || '');
    }
  }

  function debounceReinit() {
    if (reinitTimer) clearTimeout(reinitTimer);
    reinitTimer = setTimeout(init, REINIT_DEBOUNCE_MS);
  }

  // ────────────────────────────────────────────────
  // COUNTDOWN
  // ────────────────────────────────────────────────

  function getCountdownMinutes() {
    var el = document.querySelector('[data-dorie-countdown]');
    if (!el) return DEFAULT_MINUTES;
    var n = parseInt(el.getAttribute('data-minutes') || '', 10);
    return isNaN(n) || n < 1 ? DEFAULT_MINUTES : n;
  }

  function readEndTime() {
    try {
      var raw = sessionStorage.getItem(COUNTDOWN_KEY);
      if (raw) {
        var t = parseInt(raw, 10);
        if (!isNaN(t) && t > Date.now()) return t;
      }
    } catch (_) {}
    return writeEndTime();
  }

  function writeEndTime() {
    var t = Date.now() + getCountdownMinutes() * 60 * 1000;
    try { sessionStorage.setItem(COUNTDOWN_KEY, String(t)); } catch (_) {}
    return t;
  }

  function formatRemaining(ms) {
    if (ms < 0) ms = 0;
    var total = Math.floor(ms / 1000);
    var m = Math.floor(total / 60);
    var s = total % 60;
    return (m < 10 ? '0' + m : '' + m) + ':' + (s < 10 ? '0' + s : '' + s);
  }

  function tickCountdown() {
    var outs = document.querySelectorAll('[data-dorie-countdown-value]');
    if (!outs.length) {
      stopTicking();
      return;
    }
    var ends = readEndTime();
    var remaining = ends - Date.now();
    if (remaining <= 0) {
      ends = writeEndTime();
      remaining = ends - Date.now();
    }
    var text = formatRemaining(remaining);
    for (var i = 0; i < outs.length; i++) setText(outs[i], text);
  }

  function startTicking() {
    stopTicking();
    tickCountdown();
    tickTimer = setInterval(tickCountdown, 1000);
  }

  function stopTicking() {
    if (tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  }

  // ────────────────────────────────────────────────
  // SHIPPING BAR
  // ────────────────────────────────────────────────

  var shippingBarInflight = false;

  function updateShippingBar() {
    var bars = document.querySelectorAll('[data-dorie-shipping-bar]');
    if (!bars.length) return;
    if (shippingBarInflight) return;
    shippingBarInflight = true;

    // Use the un-patched fetch so we don't re-enter our own monkey-patch
    var nativeFetch = window.__dorieNativeFetch || window.fetch;
    nativeFetch(CART_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) { applyShippingBar(cart); })
      .catch(function () { /* silent */ })
      .then(function () { shippingBarInflight = false; });
  }

  function applyShippingBar(cart) {
    var bars = document.querySelectorAll('[data-dorie-shipping-bar]');
    if (!bars.length) return;

    var cartTotalCents = (cart && typeof cart.total_price === 'number') ? cart.total_price : 0;
    var cartCurrency = (cart && cart.currency ? cart.currency : '').toUpperCase();

    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var baseAmount = parseFloat(bar.getAttribute('data-threshold') || '0');
      var baseCurrency = (bar.getAttribute('data-base-currency') || '').toUpperCase();

      // Convert threshold into current presentment currency where possible.
      var thresholdCents = Math.round(baseAmount * 100);
      if (baseCurrency && cartCurrency && baseCurrency !== cartCurrency) {
        var rate = parseFloat(
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1'
        );
        if (!isNaN(rate) && rate > 0) {
          thresholdCents = Math.round(thresholdCents * rate);
        }
      }

      var remaining = Math.max(0, thresholdCents - cartTotalCents);
      var pct = thresholdCents > 0 ? Math.min(1, cartTotalCents / thresholdCents) : 0;

      var fill = bar.querySelector('[data-dorie-shipping-fill]');
      if (fill) fill.style.transform = 'scaleX(' + pct + ')';

      var labelEl = bar.querySelector('[data-dorie-shipping-label]');
      if (labelEl) {
        if (remaining <= 0) {
          var unlocked = bar.getAttribute('data-unlocked-text') || 'Complimentary worldwide shipping unlocked';
          setText(labelEl, unlocked);
          bar.classList.add('is-unlocked');
        } else {
          var formatted = formatMoney(remaining, cartCurrency || baseCurrency);
          var template = bar.getAttribute('data-remaining-text') || 'Add [amount] for complimentary shipping';
          setText(labelEl, template.replace('[amount]', formatted));
          bar.classList.remove('is-unlocked');
        }
      }
    }
  }

  // ────────────────────────────────────────────────
  // CROSS-SELL — "Complete the look"
  //   Fetches sections/dorie-cart-crosssell.liquid through the Product
  //   Recommendations API for the most recent cart item, then injects the
  //   rendered cards into the [data-dorie-crosssell] slot in the drawer.
  //   Tries intent=complementary first (Search & Discovery pairings),
  //   falls back to intent=related. Responses are cached per product so
  //   drawer re-renders (morphs) repopulate instantly without a refetch.
  // ────────────────────────────────────────────────

  var CROSSSELL_SECTION_ID = 'dorie-cart-crosssell';
  var ROUTES_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
  var crosssellCache = {}; // 'productId:intent' -> inner HTML string ('' = no results)
  var crosssellInflight = false;

  function initCrosssell() {
    var slot = document.querySelector('[data-dorie-crosssell]');
    if (!slot) return;

    var nativeFetch = window.__dorieNativeFetch || window.fetch;
    nativeFetch(CART_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) {
          emptyCrosssell(slot);
          return;
        }
        // items[0] is the most recently added line item. The signature captures
        // the full cart composition: the section's output depends on whether the
        // cart already holds a product from the source collection, so any
        // add/remove must invalidate the cached result.
        var pid = String(cart.items[0].product_id);
        var sig = cart.items
          .map(function (item) { return item.product_id; })
          .sort()
          .join('-');
        var stamp = pid + '|' + sig;
        if (slot.getAttribute('data-loaded-for') === stamp && slot.innerHTML !== '') return;
        loadCrosssell(pid, 'complementary', sig);
      })
      .catch(function () { /* silent */ });
  }

  function emptyCrosssell(slot) {
    slot.hidden = true;
    slot.innerHTML = '';
    slot.removeAttribute('data-loaded-for');
  }

  function applyCrosssell(stamp, html) {
    var slot = document.querySelector('[data-dorie-crosssell]');
    if (!slot) return;
    if (html) {
      slot.innerHTML = html;
      slot.hidden = false;
      slot.setAttribute('data-loaded-for', stamp);
    } else {
      emptyCrosssell(slot);
    }
  }

  function loadCrosssell(pid, intent, sig) {
    var key = pid + ':' + intent + ':' + sig;
    var stamp = pid + '|' + sig;
    if (typeof crosssellCache[key] === 'string') {
      if (crosssellCache[key] === '' && intent === 'complementary') {
        loadCrosssell(pid, 'related', sig);
      } else {
        applyCrosssell(stamp, crosssellCache[key]);
      }
      return;
    }
    if (crosssellInflight) return;
    crosssellInflight = true;

    var url = ROUTES_ROOT + 'recommendations/products?section_id=' + CROSSSELL_SECTION_ID
      + '&product_id=' + encodeURIComponent(pid)
      + '&limit=6&intent=' + intent;

    var nativeFetch = window.__dorieNativeFetch || window.fetch;
    nativeFetch(url, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : ''; })
      .then(function (text) {
        var holder = document.createElement('div');
        holder.innerHTML = text;
        var inner = holder.querySelector('[data-dorie-crosssell-inner]');
        var html = (inner && inner.querySelector('[data-dorie-crosssell-card]')) ? inner.outerHTML : '';
        crosssellCache[key] = html;
        crosssellInflight = false;
        if (!html && intent === 'complementary') {
          loadCrosssell(pid, 'related', sig);
        } else {
          applyCrosssell(stamp, html);
        }
      })
      .catch(function () { crosssellInflight = false; });
  }

  // One-tap add for single-variant cross-sell products (event delegation —
  // survives drawer morphs). After the add succeeds we dispatch the theme's
  // cart:update event with no sections payload, which makes Atelier's
  // cart-items-component re-render the drawer itself.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-dorie-crosssell-add]') : null;
    if (!btn || btn.tagName !== 'BUTTON') return;
    e.preventDefault();
    var variantId = parseInt(btn.getAttribute('data-variant-id') || '', 10);
    if (!variantId || btn.disabled) return;

    btn.disabled = true;
    btn.classList.add('is-adding');

    var nativeFetch = window.__dorieNativeFetch || window.fetch;
    nativeFetch(ROUTES_ROOT + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return nativeFetch(CART_ENDPOINT, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
      })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'dorie-crosssell',
              data: { source: 'dorie-crosssell', itemCount: cart.item_count },
            },
          })
        );
        debounceReinit();
      })
      .catch(function () {
        btn.disabled = false;
        btn.classList.remove('is-adding');
      });
  });

  // ────────────────────────────────────────────────
  // QUICK-ADD POPUP (cross-sell with variants)
  //   Opens a lightweight in-cart sheet instead of navigating to the PDP.
  //   Fetches ONLY /products/<handle>.js (a few KB) on click — no page-weight
  //   upfront, no PDP load. Falls back to the PDP if the fetch fails.
  // ────────────────────────────────────────────────
  var dpxEls = null;
  var dpxState = { variants: [], optionNames: [], selected: {}, url: '' };

  function dpxMoney(cents) {
    var rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1');
    if (isNaN(rate) || rate <= 0) rate = 1;
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    return formatMoney(Math.round(cents * rate), cur);
  }

  function dpxBuild() {
    if (dpxEls) return dpxEls;
    var dlg = document.createElement('dialog');
    dlg.className = 'dpx';
    dlg.setAttribute('aria-label', 'Quick add');
    dlg.innerHTML =
      '<button type="button" class="dpx__close" aria-label="Close">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
      '</button>' +
      '<div class="dpx__body">' +
        '<div class="dpx__head">' +
          '<a class="dpx__media" data-dpx-media href="#" tabindex="-1" aria-hidden="true"></a>' +
          '<div class="dpx__info">' +
            '<a class="dpx__title" data-dpx-title href="#"></a>' +
            '<span class="dpx__price" data-dpx-price></span>' +
          '</div>' +
        '</div>' +
        '<div class="dpx__options" data-dpx-options></div>' +
        '<p class="dpx__err" data-dpx-err hidden></p>' +
        '<button type="button" class="dpx__add" data-dpx-add disabled>Add to cart</button>' +
      '</div>';
    document.body.appendChild(dlg);
    dpxEls = {
      dlg: dlg,
      media: dlg.querySelector('[data-dpx-media]'),
      title: dlg.querySelector('[data-dpx-title]'),
      price: dlg.querySelector('[data-dpx-price]'),
      options: dlg.querySelector('[data-dpx-options]'),
      err: dlg.querySelector('[data-dpx-err]'),
      add: dlg.querySelector('[data-dpx-add]')
    };
    dlg.querySelector('.dpx__close').addEventListener('click', dpxClose);
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dpxClose(); });
    dpxEls.options.addEventListener('click', dpxOnOption);
    dpxEls.add.addEventListener('click', dpxOnAdd);
    return dpxEls;
  }

  function dpxClose() { if (dpxEls && dpxEls.dlg.open) dpxEls.dlg.close(); }

  function dpxVariant() {
    var names = dpxState.optionNames;
    for (var n = 0; n < names.length; n++) { if (dpxState.selected[n + 1] == null) return null; }
    return dpxState.variants.find(function (v) {
      for (var i = 0; i < names.length; i++) {
        if (v['option' + (i + 1)] !== dpxState.selected[i + 1]) return false;
      }
      return true;
    }) || null;
  }

  function dpxRefresh() {
    var v = dpxVariant();
    if (v) dpxEls.price.textContent = dpxMoney(v.price);
    if (v && !v.available) { dpxEls.err.textContent = 'Sold out in this combination'; dpxEls.err.hidden = false; }
    else { dpxEls.err.hidden = true; }
    dpxEls.add.disabled = !(v && v.available);
  }

  function dpxOnOption(e) {
    var b = e.target.closest('.dpx__opt');
    if (!b) return;
    var idx = b.getAttribute('data-opt-index');
    dpxState.selected[idx] = b.getAttribute('data-opt-value');
    var row = b.parentNode;
    var sibs = row.querySelectorAll('.dpx__opt');
    for (var i = 0; i < sibs.length; i++) sibs[i].classList.toggle('is-sel', sibs[i] === b);
    dpxRefresh();
  }

  function dpxOnAdd() {
    var v = dpxVariant();
    if (!v || !v.available) { dpxEls.err.textContent = 'Please select an option'; dpxEls.err.hidden = false; return; }
    dpxEls.add.disabled = true;
    dpxEls.add.classList.add('is-adding');
    var nativeFetch = window.__dorieNativeFetch || window.fetch;
    nativeFetch(ROUTES_ROOT + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: v.id, quantity: 1 })
    })
      .then(function (res) { if (!res.ok) throw new Error('add'); return nativeFetch(CART_ENDPOINT, { credentials: 'same-origin', headers: { Accept: 'application/json' } }); })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: 'dorie-quickadd', data: { source: 'dorie-quickadd', itemCount: cart.item_count } } }));
        debounceReinit();
        dpxClose();
      })
      .catch(function () {
        dpxEls.add.disabled = false; dpxEls.add.classList.remove('is-adding');
        dpxEls.err.textContent = 'Could not add — please try again'; dpxEls.err.hidden = false;
      });
  }

  function dpxRender(product, url) {
    var els = dpxBuild();
    dpxState.variants = product.variants || [];
    dpxState.optionNames = (product.options || []).map(function (o) { return (o && o.name) ? o.name : o; });
    dpxState.selected = {};
    dpxState.url = url || (ROUTES_ROOT + 'products/' + product.handle);

    var imgUrl = product.featured_image || (product.images && product.images[0]) || '';
    els.media.innerHTML = imgUrl ? '<img src="' + imgUrl + (imgUrl.indexOf('?') > -1 ? '&' : '?') + 'width=240" alt="" loading="lazy">' : '';
    els.media.href = dpxState.url;
    els.title.textContent = product.title;
    els.title.href = dpxState.url;
    els.price.textContent = dpxMoney(product.price);

    var html = '';
    for (var i = 0; i < dpxState.optionNames.length; i++) {
      var idx = i + 1, seen = {}, vals = [];
      for (var k = 0; k < dpxState.variants.length; k++) {
        var ov = dpxState.variants[k]['option' + idx];
        if (ov != null && !seen[ov]) { seen[ov] = true; vals.push(ov); }
      }
      html += '<div class="dpx__opt-group"><span class="dpx__opt-label">' + dpxState.optionNames[i] + '</span><div class="dpx__opt-row">';
      for (var j = 0; j < vals.length; j++) {
        var safe = String(vals[j]).replace(/"/g, '&quot;');
        html += '<button type="button" class="dpx__opt" data-opt-index="' + idx + '" data-opt-value="' + safe + '">' + vals[j] + '</button>';
      }
      html += '</div></div>';
    }
    els.options.innerHTML = html;

    // Auto-select any option that has only one value
    var groups = els.options.querySelectorAll('.dpx__opt-group');
    for (var g = 0; g < groups.length; g++) {
      var btns = groups[g].querySelectorAll('.dpx__opt');
      if (btns.length === 1) { btns[0].classList.add('is-sel'); dpxState.selected[g + 1] = btns[0].getAttribute('data-opt-value'); }
    }

    els.err.hidden = true;
    els.add.classList.remove('is-adding');
    dpxRefresh();
    if (!els.dlg.open) els.dlg.showModal();
  }

  function openQuickAdd(handle, url) {
    if (!handle) { if (url) window.location.href = url; return; }
    var nativeFetch = window.__dorieNativeFetch || window.fetch;
    nativeFetch(ROUTES_ROOT + 'products/' + handle + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { if (!res.ok) throw new Error('product'); return res.json(); })
      .then(function (product) { dpxRender(product, url); })
      .catch(function () { if (url) window.location.href = url; });
  }

  // Delegation: cross-sell "view" button → open the quick-add popup
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-dorie-crosssell-view]') : null;
    if (!btn) return;
    e.preventDefault();
    openQuickAdd(btn.getAttribute('data-handle'), btn.getAttribute('data-url'));
  });

  // ────────────────────────────────────────────────
  // INIT + REBIND
  // ────────────────────────────────────────────────

  // ────────────────────────────────────────────────
  // "YOU WON" SHIPPING CELEBRATION
  //   The threshold is low ($100) so the cart is almost always already
  //   unlocked. Each time the drawer opens we replay the fill sweep + a quiet
  //   shimmer across the label so the win registers. Pure transform/opacity.
  // ────────────────────────────────────────────────
  function sweepShipping() {
    var bars = document.querySelectorAll('[data-dorie-shipping-bar]');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var fill = bar.querySelector('[data-dorie-shipping-fill]');
      if (fill) {
        var target = fill.style.transform || 'scaleX(1)';
        // Reset to 0 (no transition) → reflow → sweep to target.
        fill.style.transition = 'none';
        fill.style.transform = 'scaleX(0)';
        void fill.offsetWidth;
        fill.style.transition = '';
        fill.style.transform = target;
      }
      // Re-trigger the shimmer/glow (remove → reflow → add).
      bar.classList.remove('dorie-shipping-celebrate');
      void bar.offsetWidth;
      bar.classList.add('dorie-shipping-celebrate');
    }
  }

  var drawerOpenWatched = false;
  function watchDrawerOpen() {
    if (drawerOpenWatched) return;
    var comp = document.querySelector('cart-drawer-component');
    if (!comp) return;
    drawerOpenWatched = true;
    // DialogComponent dispatches 'dialog:open' on the component when it opens.
    comp.addEventListener('dialog:open', function () {
      requestAnimationFrame(sweepShipping);
    });
  }

  function init() {
    // Countdown: only starts if the element is present (e.g. cart not empty)
    if (document.querySelector('[data-dorie-countdown-value]')) {
      startTicking();
    } else {
      stopTicking();
    }

    // Shipping bar: only fires if the element is present
    if (document.querySelector('[data-dorie-shipping-bar]')) {
      updateShippingBar();
    }

    // Cross-sell: only fires if the drawer slot is present
    initCrosssell();

    // Celebration: attach the drawer-open listener (once)
    watchDrawerOpen();
  }

  /**
   * Monkey-patch fetch so we can reliably detect cart mutations. We cache the
   * original on window.__dorieNativeFetch so updateShippingBar can use the
   * unpatched version and avoid recursion.
   */
  function patchFetch() {
    if (fetchPatched || typeof window.fetch !== 'function') return;
    fetchPatched = true;
    window.__dorieNativeFetch = window.fetch.bind(window);

    var original = window.fetch;
    window.fetch = function (input, opts) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isMutation = false;
      if (url) {
        for (var i = 0; i < CART_MUTATION_URLS.length; i++) {
          if (url.indexOf(CART_MUTATION_URLS[i]) !== -1) { isMutation = true; break; }
        }
      }
      var p = original.apply(this, arguments);
      if (isMutation) {
        p.then(function () { debounceReinit(); }).catch(function () {});
      }
      return p;
    };
  }

  /**
   * MutationObserver on cart-items-component. Fires whenever Atelier's morph.js
   * rebuilds the list (e.g. after add/remove). We debounce aggressively and
   * update text nodes in place during ticks so this observer never self-loops.
   */
  function attachObservers() {
    if (observersAttached) return;
    var targets = [
      document.querySelector('cart-items-component'),
      document.querySelector('cart-drawer-component'),
    ].filter(Boolean);
    if (!targets.length) return;

    observersAttached = true;
    var mo = new MutationObserver(function (records) {
      // Skip if the only thing that changed was our own countdown text node.
      // Because we mutate firstChild.data in place, childList mutations from
      // us shouldn't fire — but belt-and-suspenders.
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        var t = r.target;
        if (t && (t.hasAttribute && (t.hasAttribute('data-dorie-countdown-value')))) continue;
        debounceReinit();
        return;
      }
    });
    targets.forEach(function (t) {
      mo.observe(t, { childList: true, subtree: true });
    });
  }

  // Pause countdown when the tab is hidden to save battery
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopTicking();
    else startTicking();
  });

  // Shopify theme-level events some themes dispatch
  document.addEventListener('cart:update', debounceReinit);
  document.addEventListener('cart:updated', debounceReinit);
  document.addEventListener('cart:refresh', debounceReinit);

  // Back/forward cache restores
  window.addEventListener('pageshow', debounceReinit);

  // Boot
  function boot() {
    patchFetch();
    init();
    attachObservers();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();

/* ── Cart sale countdown ──────────────────────────────────────────────
   Reads the same rolling window as the announcement bar and the
   collection-page countdown, so all three show one clock and reset
   together. The bar owns the key; this only reads it, opening a window
   itself only if a shopper lands straight in the cart. One global ticker,
   re-querying the element each second so it survives the cart
   re-rendering on add/remove. */
(function () {
  'use strict';
  var KEY = 'dorie-sale-timer-end';
  var WINDOW_MS = 2 * 60 * 60 * 1000;

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function endMs() {
    /* The announcement bar's shared sale clock owns this key. */
    if (window.DORIE_SALE) return window.DORIE_SALE.endMs();
    var now = Date.now();
    var t = 0;
    try {
      var v = localStorage.getItem(KEY);
      if (v) {
        t = parseInt(v, 10) || 0;
        if (t && t < 1e11) t *= 1000; /* legacy seconds */
      }
    } catch (e) {}
    if (!t || t <= now) {
      t = now + WINDOW_MS;
      try { localStorage.setItem(KEY, String(t)); } catch (e) {}
    }
    return t;
  }

  function tick() {
    var timeEl = document.getElementById('dorie-cart-cd-time');
    var el     = document.getElementById('dorie-cart-cd-msg');
    if (!timeEl && !el) return;

    var diff = Math.max(0, endMs() - Date.now());
    var clock;
    if (window.DORIE_SALE) {
      clock = window.DORIE_SALE.format(diff);
    } else {
      var h = Math.floor(diff / 3600000);
      var m = Math.floor(diff / 60000) % 60;
      var sec = Math.floor(diff / 1000) % 60;
      clock = pad(h) + ':' + pad(m) + ':' + pad(sec);
    }

    if (timeEl) {
      /* Digits live in their own span so they can take the oxblood the
         announcement bar's timer uses, with the label left neutral. */
      timeEl.textContent = clock;
    } else {
      /* Older markup without the split span. */
      el.textContent = (window.DORIE_SALE ? window.DORIE_SALE.label() : 'Sale') + ' ends \u2013 ' + clock;
    }
  }

  tick();
  setInterval(tick, 1000);
})();
